import React, { useCallback } from 'react';
import { Button, TextArea, Select, CodeBlockHighlighted } from 'plaid-threads';
import axios from 'axios';
import './index.scss';

const ACCOUNT_TYPE_OPTIONS = [
  { label: 'Depository', value: 'depository' },
  { label: 'Credit', value: 'credit' },
];

const PFC_VERSION_OPTIONS = [
  { label: 'v2', value: 'v2' },
  { label: 'v1', value: 'v1' },
];

const BATCH_SIZE = 100;
const MAX_TRANSACTIONS = 10000;

const SAMPLE_JSON = `[
  {
    "id": "103",
    "description": "HOME DEPOT",
    "amount": 115.23,
    "direction": "OUTFLOW",
    "iso_currency_code": "GBP",
    "location": {
      "city": "CAMPBELL",
      "region": "CA"
    },
    "mcc": "5310",
    "date_posted": "2022-07-05"
  },
  {
    "id": "3958434bhde9384bcmeo3401",
    "description": "DD DOORDASH BURGERKIN 855-123-4567 CA",
    "amount": 28.34,
    "direction": "OUTFLOW",
    "iso_currency_code": "USD"
  }
]`;

const EnrichData: React.FC = () => {
  const [jsonInput, setJsonInput] = React.useState('');
  const [enrichedData, setEnrichedData] = React.useState<any>(null);
  const [error, setError] = React.useState('');
  const [warning, setWarning] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [accountType, setAccountType] = React.useState('depository');
  const [pfcVersion, setPfcVersion] = React.useState('v2');

  const convertCsvToJson = useCallback((csv: string): any[] => {
    try {
      // Split into lines and filter out empty lines
      const lines = csv
        .trim()
        .split('\n')
        .filter((line) => line.trim());
      if (lines.length < 2)
        throw new Error('CSV must have a header row and at least one data row');

      // Parse headers, ensuring we handle any BOM characters
      const headers = lines[0]
        .replace(/^\uFEFF/, '')
        .split(',')
        .map((h) => h.trim());
      const requiredFields = [
        'id',
        'description',
        'amount',
        'direction',
        'iso_currency_code',
      ];
      const missingFields = requiredFields.filter(
        (field) => !headers.includes(field),
      );

      if (missingFields.length > 0) {
        throw new Error(`missing required fields: ${missingFields.join(', ')}`);
      }

      return lines.slice(1).map((line) => {
        const values = line.split(',').map((v) => v.trim());
        const obj: any = {};

        headers.forEach((header, index) => {
          const value = values[index];
          if (value !== undefined && value !== '') {
            if (header === 'amount') {
              obj[header] = parseFloat(value);
            } else if (header === 'city' || header === 'region') {
              if (!obj.location) obj.location = {};
              if (value) obj.location[header] = value;
            } else {
              obj[header] = value;
            }
          }
        });

        return obj;
      });
    } catch (err: any) {
      throw new Error(`Invalid CSV format: ${err.message}`);
    }
  }, []);

  const detectFormat = useCallback((input: string): 'json' | 'csv' => {
    input = input.trim();
    // Check if it starts with the expected CSV header
    if (
      input
        .toLowerCase()
        .startsWith('id,description,amount,direction,iso_currency_code')
    ) {
      return 'csv';
    }
    // Check if it's JSON
    try {
      JSON.parse(input);
      return 'json';
    } catch {
      // If it contains commas and newlines, it's probably a CSV
      if (input.includes(',') && input.includes('\n')) {
        return 'csv';
      }
      return 'json';
    }
  }, []);

  const validateAndProcessInput = useCallback(
    (input: string) => {
      try {
        const format = detectFormat(input);

        let parsedData: any[];
        if (format === 'csv') {
          parsedData = convertCsvToJson(input);
        } else {
          parsedData = JSON.parse(input);
          if (!Array.isArray(parsedData)) {
            throw new Error('Input must be a JSON array of transactions');
          }
        }

        setWarning('');

        if (parsedData.length > MAX_TRANSACTIONS) {
          throw new Error(
            `Maximum of ${MAX_TRANSACTIONS.toLocaleString()} transactions allowed. You provided ${parsedData.length.toLocaleString()}.`,
          );
        }

        if (parsedData.length > BATCH_SIZE) {
          setWarning(
            `${parsedData.length} transactions will be processed in ${Math.ceil(parsedData.length / BATCH_SIZE)} batches of ${BATCH_SIZE}`,
          );
        }

        return parsedData;
      } catch (err: any) {
        throw new Error(err.message || 'Invalid input format');
      }
    },
    [detectFormat, convertCsvToJson, setWarning],
  );

  const handleFileUpload = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const content = e.target?.result as string;
            validateAndProcessInput(content); // Validate but don't process yet
            setJsonInput(content);
            setError('');
          } catch (err: any) {
            setError(err.message);
          }
        };
        reader.readAsText(file);
      }
    },
    [validateAndProcessInput, setJsonInput, setError],
  );

  const handleEnrich = async () => {
    try {
      const processedJson = validateAndProcessInput(jsonInput);
      setLoading(true);
      setError('');
      setEnrichedData(null); // Clear previous response

      const isLargeBatch = processedJson.length > 100;
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/enrich`,
        {
          account_type: accountType,
          transactions: processedJson,
          pfc_version: pfcVersion,
        },
        {
          withCredentials: true,
          ...(isLargeBatch ? { responseType: 'blob' } : {}),
        },
      );

      if (isLargeBatch) {
        // Trigger browser file download
        const blob = new Blob([response.data], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download =
          response.headers['content-disposition']?.match(
            /filename="(.+)"/,
          )?.[1] || 'enriched_transactions.json';
        a.click();
        window.URL.revokeObjectURL(url);
        setEnrichedData({ download: true, filename: a.download });
      } else {
        setEnrichedData(response.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to enrich data');
    } finally {
      setLoading(false);
    }
  };

  const loadExample = () => {
    setJsonInput(SAMPLE_JSON);
    setError('');
    setWarning('');
  };

  return (
    <div className="EnrichData">
      <div className="LandingV2__title">Enrich Sample Data</div>
      <div style={{ marginBottom: '10px' }}>
        Select the account type you'd like to enrich the transactions for.
      </div>
      <div className="EnrichData__account-type">
        <Select
          id="account-type"
          label=""
          options={ACCOUNT_TYPE_OPTIONS}
          value={ACCOUNT_TYPE_OPTIONS.find((opt) => opt.value === accountType)}
          onChange={(option: any) => setAccountType(option.value)}
        />
      </div>
      <div style={{ marginBottom: '10px' }}>
        Select the Personal Finance Category (PFC) version.
      </div>
      <div className="EnrichData__account-type">
        <Select
          id="pfc-version"
          label=""
          options={PFC_VERSION_OPTIONS}
          value={PFC_VERSION_OPTIONS.find((opt) => opt.value === pfcVersion)}
          onChange={(option: any) => setPfcVersion(option.value)}
        />
      </div>
      <div style={{ marginBottom: '10px' }}>
        Transactions are automatically batched into groups of {BATCH_SIZE} per
        API request. Each transaction should include:
        <ul style={{ margin: '4px 0 4px 10px' }}>
          <li>description</li>
          <li>amount</li>
          <li>direction (INFLOW | OUTFLOW)</li>
          <li>iso_currency_code</li>
          <li>id</li>
          <li>location (optional)</li>
          <li>date_posted (optional)</li>
        </ul>
        <p>
          Input your sample transactions below or upload them from a file. Not
          sure what to upload? See{' '}
          <span
            style={{
              color: '#0a85ea',
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
            onClick={loadExample}
          >
            this example
          </span>
        </p>
      </div>

      <TextArea
        id="json-input"
        label=""
        value={jsonInput}
        onChange={(e) => {
          setJsonInput(e.target.value);
          setError('');
          setWarning('');
        }}
        placeholder="Paste your sample transactions here"
        className="EnrichData__textarea"
      />
      <div className="EnrichData__file-input">
        <input type="file" accept=".json,.csv" onChange={handleFileUpload} />
      </div>

      {error && <div className="EnrichData__error">{error}</div>}

      {warning && <div className="EnrichData__warning">{warning}</div>}

      <div className="EnrichData__button">
        <Button
          wide
          centered
          onClick={handleEnrich}
          disabled={!jsonInput || loading}
        >
          {loading ? 'Processing...' : 'Enrich Data'}
        </Button>
      </div>

      {enrichedData && enrichedData.download && (
        <div
          style={{
            padding: '20px',
            background: '#e8f5e9',
            borderRadius: '8px',
          }}
        >
          <div style={{ fontWeight: 'bold' }}>
            Enriched transactions downloaded as {enrichedData.filename}
          </div>
        </div>
      )}

      {enrichedData && !enrichedData.download && (
        <CodeBlockHighlighted
          lang="json"
          code={JSON.stringify(enrichedData, null, 2)}
          onClick={() =>
            navigator.clipboard.writeText(JSON.stringify(enrichedData, null, 2))
          }
          title="Enriched Data Response"
        />
      )}
    </div>
  );
};

export default EnrichData;
